import { useEffect, useMemo, useState } from 'react';
import { Button, Card, makeStyles, Spinner, Text, tokens } from '@fluentui/react-components';
import { StatusPill } from './StatusPill';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/TeamsAuthProvider';
import { api } from '../../services/api';
import { hasEmulationAccess } from '../../utils/access';
import type { AdminCustomer, EmulationTarget, UserInfo } from '../../types';

// User-visible copy lives together here (not scattered inline) so a future
// French translation pass can work from whole sentences with placeholders
// instead of hunting down concatenated fragments.
const STRINGS = {
  panelTitlePlatformAdmin: 'Platform Admin View As',
  panelTitleTenantAdmin: 'View As',
  // Platform admins are Logic V staff -- this copy is internal by design.
  panelDescriptionPlatformAdmin:
    'Switch the app into a lower-role read-only view so we can validate what a customer user sees.',
  // Tenant admins are a paying customer's own admin -- speak to them, not
  // about them.
  panelDescriptionTenantAdmin:
    'View the app as one of your users, read-only, to see exactly what they see.',
  errorSelectCustomerAndUser: 'Select a customer and user to emulate.',
  errorSelectUser: 'Select a user to emulate.',
};

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    width: '100%',
    marginBottom: tokens.spacingVerticalM,
  },
  banner: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    borderLeft: `3px solid ${tokens.colorPaletteYellowBorder2}`,
    backgroundColor: tokens.colorPaletteYellowBackground1,
    minHeight: '40px',
  },
  bannerText: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
  bannerActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    alignItems: 'center',
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalM,
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  select: {
    minHeight: '36px',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    padding: `0 ${tokens.spacingHorizontalS}`,
    color: tokens.colorNeutralForeground1,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
  },
  helper: {
    color: tokens.colorNeutralForeground3,
  },
});

function getHomePath(user: UserInfo) {
  if (user.role === 'platform_admin' || user.role === 'tenant_admin') {
    return '/dashboard';
  }
  if (user.role === 'branch_manager') {
    return '/branch';
  }
  return '/my';
}

function buildTargetLabel(target: EmulationTarget) {
  const base = target.display_name && target.display_name !== target.user_email
    ? `${target.display_name} (${target.user_email})`
    : target.user_email;

  const branchSummary =
    target.branch_names.length > 0 ? ` · ${target.branch_names.join(', ')}` : '';
  const numberSummary =
    target.owned_number_count > 0
      ? ` · ${target.owned_number_count} ${target.owned_number_count === 1 ? 'number' : 'numbers'}`
      : '';
  const activitySummary =
    target.recent_call_count_30d > 0 ? ` · ${target.recent_call_count_30d} calls / 30d` : ' · no recent calls';

  return `${base} · ${target.role}${branchSummary}${numberSummary}${activitySummary}`;
}

export interface PlatformEmulationControlProps {
  /** Controlled from App.tsx so the trigger button (in every route's
   * PageHeader actions slot, see ViewAsAction) and this panel share one open
   * state. */
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PlatformEmulationControl({ isOpen, onOpenChange }: PlatformEmulationControlProps) {
  const styles = useStyles();
  const navigate = useNavigate();
  const { user, startEmulation, clearEmulation } = useAuth();
  const [customers, setCustomers] = useState<AdminCustomer[]>([]);
  const [targets, setTargets] = useState<EmulationTarget[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [selectedUserEmail, setSelectedUserEmail] = useState<string>('');
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [isLoadingTargets, setIsLoadingTargets] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // tenant_admin gets View As too, confined to their own customer by the
  // backend. Keep the platform-admin distinction: only they pick a customer.
  const isPlatformAdmin = user?.actual_role === 'platform_admin';
  const isTenantAdmin = user?.actual_role === 'tenant_admin';
  const canEmulate = hasEmulationAccess(user);

  useEffect(() => {
    if (!user?.is_emulating) {
      return;
    }

    if (user.emulated_customer_account_id) {
      setSelectedCustomer(user.emulated_customer_account_id);
    }
    if (user.emulated_user_email) {
      setSelectedUserEmail(user.emulated_user_email);
    }
  }, [user?.emulated_customer_account_id, user?.emulated_user_email, user?.is_emulating]);

  useEffect(() => {
    if (isTenantAdmin && user?.customer_account_id && !selectedCustomer) {
      setSelectedCustomer(user.customer_account_id);
    }
  }, [isTenantAdmin, user?.customer_account_id, selectedCustomer]);

  useEffect(() => {
    if (!isPlatformAdmin || !isOpen || customers.length > 0) {
      return;
    }

    let isMounted = true;
    setIsLoadingCustomers(true);
    setError(null);

    void api.getAdminCustomers()
      .then((result) => {
        if (!isMounted) {
          return;
        }
        setCustomers(result);

        if (!selectedCustomer && result.length > 0) {
          const preferred = user?.emulated_customer_account_id ?? result[0].account_id;
          setSelectedCustomer(preferred);
        }
      })
      .catch((err: unknown) => {
        if (isMounted) {
          setError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingCustomers(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [customers.length, isOpen, isPlatformAdmin, selectedCustomer, user?.emulated_customer_account_id]);

  useEffect(() => {
    if (!canEmulate || !isOpen || !selectedCustomer) {
      return;
    }

    let isMounted = true;
    setIsLoadingTargets(true);
    setError(null);

    void api.getEmulationTargets(selectedCustomer)
      .then((result) => {
        if (!isMounted) {
          return;
        }

        setTargets(result);
        if (!result.some((target) => target.user_email === selectedUserEmail)) {
          const preferred =
            user?.is_emulating && user.emulated_customer_account_id === selectedCustomer
              ? user.emulated_user_email
              : result[0]?.user_email;
          setSelectedUserEmail(preferred ?? '');
        }
      })
      .catch((err: unknown) => {
        if (isMounted) {
          setError(err instanceof Error ? err.message : String(err));
          setTargets([]);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingTargets(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [
    canEmulate,
    isOpen,
    selectedCustomer,
    selectedUserEmail,
    user?.emulated_customer_account_id,
    user?.emulated_user_email,
    user?.is_emulating,
  ]);

  const selectedTarget = useMemo(
    () => targets.find((target) => target.user_email === selectedUserEmail) ?? null,
    [selectedUserEmail, targets]
  );

  if (!canEmulate || !user) {
    return null;
  }

  const onApply = async () => {
    if (!selectedCustomer || !selectedUserEmail) {
      // The customer picker is only rendered for platform_admin (it's fixed
      // to their own customer for tenant_admin), so the message they see
      // should only ask for what they're actually missing.
      setError(isPlatformAdmin ? STRINGS.errorSelectCustomerAndUser : STRINGS.errorSelectUser);
      return;
    }

    try {
      setIsApplying(true);
      setError(null);
      const emulatedUser = await startEmulation(selectedCustomer, selectedUserEmail);
      onOpenChange(false);
      navigate(getHomePath(emulatedUser));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsApplying(false);
    }
  };

  const onClear = async () => {
    try {
      setIsApplying(true);
      setError(null);
      const actualUser = await clearEmulation();
      navigate(getHomePath(actualUser));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div className={styles.wrapper} data-testid="platform-emulation-control">
      {user.is_emulating && (
        <div className={styles.banner} data-testid="platform-emulation-banner">
          <div className={styles.bannerText}>
            <StatusPill tone="info">View As</StatusPill>
            <Text weight="semibold" size={300}>
              {user.emulated_user_name || user.emulated_user_email}
            </Text>
            <Text size={300}>
              {user.role.replace('_', ' ')} at {user.emulated_customer_name || user.customer_filter || user.emulated_customer_account_id}
            </Text>
          </div>
          <div className={styles.bannerActions}>
            <Button size="small" appearance="secondary" onClick={() => onOpenChange(!isOpen)}>
              Change
            </Button>
            <Button size="small" appearance="secondary" onClick={() => void onClear()} disabled={isApplying}>
              Exit
            </Button>
          </div>
        </div>
      )}

      {/* The "View As" trigger button itself now lives in every route's
          PageHeader actions area (ViewAsAction), not here as a floating strip
          (finding F8) -- it shares this component's isOpen/onOpenChange
          state via props from App.tsx. */}

      {isOpen && (
        <Card className={styles.panel} data-testid="platform-emulation-panel">
          <Text weight="semibold">
            {isPlatformAdmin ? STRINGS.panelTitlePlatformAdmin : STRINGS.panelTitleTenantAdmin}
          </Text>
          <Text className={styles.helper}>
            {isPlatformAdmin ? STRINGS.panelDescriptionPlatformAdmin : STRINGS.panelDescriptionTenantAdmin}
          </Text>

          <div className={styles.formRow}>
            {isPlatformAdmin && (
              <div className={styles.field}>
                <Text size={300}>Customer</Text>
                <select
                  data-testid="platform-emulation-customer"
                  className={styles.select}
                  value={selectedCustomer}
                  onChange={(event) => {
                    setSelectedCustomer(event.target.value);
                    setSelectedUserEmail('');
                  }}
                  disabled={isLoadingCustomers || isApplying}
                >
                  <option value="">Select customer</option>
                  {customers.map((customer) => (
                    <option key={customer.account_id} value={customer.account_id}>
                      {customer.account_name || customer.account_id}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className={styles.field}>
              <Text size={300}>User</Text>
              <select
                data-testid="platform-emulation-user"
                className={styles.select}
                value={selectedUserEmail}
                onChange={(event) => setSelectedUserEmail(event.target.value)}
                disabled={!selectedCustomer || isLoadingTargets || isApplying}
              >
                <option value="">Select user</option>
                {targets.map((target) => (
                  <option key={target.user_email} value={target.user_email}>
                    {buildTargetLabel(target)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {(isLoadingCustomers || isLoadingTargets) && <Spinner label="Loading emulation options..." />}

          {selectedTarget && (
            <Text className={styles.helper}>
              Target role: {selectedTarget.role.replace('_', ' ')} · {selectedTarget.recent_call_count_30d} calls in the last 30 days
            </Text>
          )}

          {error && <Text className={styles.error}>{error}</Text>}

          <div className={styles.actions}>
            <Button
              data-testid="platform-emulation-apply"
              appearance="primary"
              onClick={() => void onApply()}
              disabled={!selectedCustomer || !selectedUserEmail || isApplying}
            >
              {isApplying ? 'Applying...' : 'Apply View As'}
            </Button>
            {user.is_emulating && (
              <Button data-testid="platform-emulation-exit" appearance="secondary" onClick={() => void onClear()} disabled={isApplying}>
                Exit View As
              </Button>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
